import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AuthStateResponse, InviteInfoResponse, OkResponse } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Alert, Button, Spinner, TextField } from '../../components/ui';
import { AuthLayout, useProceed } from './AuthLayout';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const forgot = useMutation({
    mutationFn: () => api.post('/auth/password/forgot', { body: { email } }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    forgot.mutate();
  };
  return (
    <AuthLayout title={t('auth.forgotTitle')}>
      {forgot.isSuccess ? (
        <div className="space-y-4">
          <Alert kind="success">{t('auth.forgotSent')}</Alert>
          <Link className="text-brand underline" to="/login">
            {t('auth.backToSignIn')}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorLine error={forgot.error} />
          <TextField label={t('auth.email')} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" variant="primary" className="w-full" disabled={forgot.isPending}>
            {t('auth.sendResetLink')}
          </Button>
          <p className="text-center text-sm">
            <Link className="text-brand underline" to="/login">
              {t('auth.backToSignIn')}
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const reset = useMutation({
    mutationFn: () => api.post('/auth/password/reset', { body: { token, newPassword: password }, schema: OkResponse }),
  });
  const errs = fieldErrors(reset.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    reset.mutate();
  };
  return (
    <AuthLayout title={t('auth.resetPassword')}>
      {reset.isSuccess ? (
        <div className="space-y-4">
          <Alert kind="success">{t('auth.resetDone')}</Alert>
          <Link className="text-brand underline" to="/login">
            {t('auth.signIn')}
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorLine error={reset.error} />
          <TextField
            label={t('auth.newPassword')}
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errs['newPassword']}
          />
          <Button type="submit" variant="primary" className="w-full" disabled={reset.isPending || !token}>
            {t('auth.resetPassword')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function InvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const proceed = useProceed();
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const info = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get(`/auth/invite/${encodeURIComponent(token)}`, { schema: InviteInfoResponse }),
    enabled: token.length > 0,
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () =>
      api.post('/auth/invite/accept', {
        body: {
          token,
          ...(info.data?.needsPassword ? { password } : {}),
          ...(!info.data?.fullName && fullName ? { fullName } : {}),
        },
        schema: AuthStateResponse,
      }),
    onSuccess: proceed,
  });
  const errs = fieldErrors(accept.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    accept.mutate();
  };
  return (
    <AuthLayout title={t('auth.acceptInvite')}>
      {!token ? (
        <Alert>{t('error.token_invalid')}</Alert>
      ) : info.isPending ? (
        <Spinner />
      ) : info.isError ? (
        <ErrorPanel error={info.error} />
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm">{t('auth.inviteTitle', { tenant: info.data.tenantName ?? t('app.name') })}</p>
          <p className="text-sm text-slate-600">{info.data.emailMasked}</p>
          <ErrorLine error={accept.error} />
          {info.data.fullName ? null : (
            <TextField label={t('auth.fullName')} required value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" error={errs['fullName']} />
          )}
          {info.data.needsPassword ? (
            <TextField
              label={t('auth.newPassword')}
              type="password"
              autoComplete="new-password"
              minLength={info.data.passwordMinLength}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint={t('auth.passwordRule', { min: info.data.passwordMinLength })}
              error={errs['password']}
            />
          ) : null}
          <Button type="submit" variant="primary" className="w-full" disabled={accept.isPending}>
            {t('auth.acceptInvite')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
